import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTACT_EMAIL, FORMSPREE_ENDPOINT } from '../data/products';
import { btnGhost, btnPrimary } from './ui';

const inputClass =
  'mt-1.5 w-full rounded-xl border border-line bg-ink px-3.5 py-3 text-sm text-paper placeholder:text-muted/70 focus:border-amber focus:outline-none focus:ring-2 focus:ring-amber/40';

const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    interest: 'diagnosis',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company,
          phone: form.phone,
          interest: form.interest,
          message: form.message,
          _subject: `Simpian Consulting — discovery request — ${form.name}`,
          _replyto: form.email,
        }),
      });
      if (!response.ok) throw new Error('form failed');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="rounded-2xl border border-mint/30 bg-panel p-8 text-center" role="status">
        <h3 className="font-display text-2xl text-paper">{t('cta.thanksTitle')}</h3>
        <p className="mt-3 text-muted">{t('cta.thanks')}</p>
        <p className="mt-4 text-sm text-muted">
          <a className="text-amber hover:text-mint" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate={false}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm text-muted">
          {t('cta.name')}
          <input className={inputClass} name="name" required autoComplete="name" placeholder={t('cta.namePh')} value={form.name} onChange={onChange} />
        </label>
        <label className="block text-sm text-muted">
          {t('cta.email')}
          <input className={inputClass} type="email" name="email" required autoComplete="email" placeholder={t('cta.emailPh')} value={form.email} onChange={onChange} />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm text-muted">
          {t('cta.company')}
          <input className={inputClass} name="company" autoComplete="organization" placeholder={t('cta.companyPh')} value={form.company} onChange={onChange} />
        </label>
        <label className="block text-sm text-muted">
          {t('cta.phone')}
          <input className={inputClass} type="tel" name="phone" autoComplete="tel" placeholder={t('cta.phonePh')} value={form.phone} onChange={onChange} />
        </label>
      </div>
      <label className="block text-sm text-muted">
        {t('cta.interest')}
        <select className={inputClass} name="interest" value={form.interest} onChange={onChange}>
          <option value="diagnosis">{t('cta.interests.diagnosis')}</option>
          <option value="automation">{t('cta.interests.automation')}</option>
          <option value="product">{t('cta.interests.product')}</option>
          <option value="other">{t('cta.interests.other')}</option>
        </select>
      </label>
      <label className="block text-sm text-muted">
        {t('cta.message')}
        <textarea className={inputClass} name="message" required rows={4} placeholder={t('cta.messagePh')} value={form.message} onChange={onChange} />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className={btnPrimary} disabled={status === 'sending'}>
          {status === 'sending' ? t('cta.sending') : t('cta.submit')}
        </button>
        <a className={btnGhost} href={`mailto:${CONTACT_EMAIL}`}>{t('cta.orEmail')}</a>
      </div>
      {status === 'error' && (
        <p className="text-sm text-coral" role="alert">{t('cta.fail')}</p>
      )}
      <p className="text-xs text-muted">{t('cta.privacy')}</p>
    </form>
  );
};

export default ContactForm;
